import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowRight,
  BookOpen,
  Bug,
  HardDrive,
  Inbox,
  KeyRound,
  Upload,
  Zap
} from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { CopyableId } from '@/components/CopyableId';
import { DetonationTag } from '@/components/DetonationTag';
import { MachinePoolCard, poolTotals } from '@/components/dashboard/MachinePoolCard';
import { SubmissionsChart, submissionsByDay } from '@/components/dashboard/SubmissionsChart';
import { TopFamilies } from '@/components/dashboard/TopFamilies';
import OrgPicker, { ALL_ORGS, useActiveOrg } from '@/components/OrgPicker';
import { StatusBadge } from '@/components/StatusBadge';
import { getFileIcon } from '@/components/SubmissionIcon';
import { type Column, EmptyState, ErrorState, ResponsiveTable } from '@/components/ui';
import { VerdictBadge } from '@/components/VerdictBadge';
import { useAuth } from '@/context/AuthContext';
import { canBrandOrg, OrgLogoPrompt } from '@/features/branding/OrgLogoCard';
import { api, type OrgInflight, SUBMISSION_PAGE_SIZE, type Submission } from '@/lib/api';
import {
  formatRelativeTime,
  formatSubmittedSize,
  formatTimeoutShort,
  formatWallDuration,
} from '@/lib/format';
import { TASK_STREAM_KINDS, useSSE } from '@/lib/sse';
import { periodLabel } from '@/lib/usage';

/** How far back the submissions chart reaches. Two weeks is the window the
 *  list endpoint's page comfortably covers, so the chart never implies history
 *  the query did not fetch. */
const SUBMISSION_CHART_DAYS = 14;

export default function DashboardPage() {
  const { user } = useAuth();
  const { orgId } = useActiveOrg();
  const qc = useQueryClient();
  const isAllOrgs = orgId === ALL_ORGS;
  const activeOrgName = isAllOrgs
    ? null
    : (user?.memberships?.find((m) => m.org_id === orgId)?.org_name ?? null);

  const primaryOrgId =
    (isAllOrgs ? null : orgId) ??
    user?.memberships?.find((m) => m.role === 'manager' || m.role === 'org_admin')?.org_id ?? 
    user?.memberships?.[0]?.org_id ?? null;

  const subsQ = useQuery({
    // The newest page, and deliberately the same key and query the
    // submissions page asks for as its first page: the two surfaces share one
    // cache entry instead of fetching the same rows twice. Deeper pages are
    // the submissions page's business, and it is the one with a pager.
    queryKey: ['submissions', orgId, null, SUBMISSION_PAGE_SIZE, 0],
    queryFn: () => api.listSubmissions(orgId!, { limit: SUBMISSION_PAGE_SIZE }),
    enabled: !!orgId,
    refetchInterval: 20_000,
  });

  // The feed's own recent intake, for the families chart. Cached deployment-wide
  // by the orchestrator, so this poll costs nothing outbound.
  //
  // Enabled on the all-orgs view too, unlike the quota beside it: this
  // describes the FEED rather than any one tenant, so there is no per-org
  // answer for it to be wrong about.
  const activityQ = useQuery({
    queryKey: ['intel-activity', orgId],
    queryFn: () => api.intelActivity(orgId!),
    enabled: !!orgId,
    staleTime: 10 * 60_000,
    retry: false,
  });

  const quotaQ = useQuery({
    queryKey: ['quota', orgId],
    queryFn: () => api.getQuota(orgId!),
    enabled: !!orgId && !isAllOrgs,
    refetchInterval: 30_000,
  });

  // Platform console (all-orgs) source: fleet capacity + per-org
  // utilization from the admin inflight summary.
  const fleetQ = useQuery({
    queryKey: ['admin:inflight'],
    queryFn: () => api.getOrchestrationInflight(),
    enabled: isAllOrgs,
    refetchInterval: 10_000,
  });

  // The org view's pool source: the member-visible intake catalog. The SAME
  // key and queryFn the submit page holds ('systems') - deliberately, so the
  // two share one cache entry; with both mounted the shorter interval wins.
  const systemsQ = useQuery({
    queryKey: ['systems'],
    queryFn: () => api.listSystemImages(),
    enabled: !isAllOrgs,
    refetchInterval: 15_000,
  });

  // A query retrying after a failure must not keep showing the old error.
  // TanStack holds status 'error' across the refetch, so isLoading is false and
  // the stale message would sit there looking current - which is what made a
  // tab left open across a deploy read as permanently broken.
  const subsLoading = subsQ.isFetching && subsQ.data === undefined;
  const fleetLoading = fleetQ.isFetching && fleetQ.data === undefined;

  useSSE(
    orgId && !isAllOrgs ? `/api/sse/customer?org_id=${orgId}` : null,
    () => {
      qc.invalidateQueries({ queryKey: ['submissions', orgId] });
      qc.invalidateQueries({ queryKey: ['quota', orgId] });
    },
    TASK_STREAM_KINDS,
  );

  const subs = subsQ.data?.submissions ?? [];
  const recent = subs.slice(0, 6);
  const byDay = useMemo(() => submissionsByDay(subs, SUBMISSION_CHART_DAYS), [subs]);

  // md+ table shape. Size and Organization drop out first when the column
  // runs narrow - the card view below md carries all of them regardless.
  const recentColumns: Column<Submission>[] = [
    {
      key: 'target',
      header: 'Target',
      cell: (s) => (
        <div className="cell-file">
          {getFileIcon(s.target_name, undefined, s.target_kind)}
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-medium wrap-anywhere">{s.target_name}</span>
              <DetonationTag kind={s.detonation_kind} lane={s.lane} />
            </div>
            <div className="cell-id text-xs">
              <CopyableId id={s.malbox_task_id} className="-ml-0" />
            </div>
          </div>
        </div>
      ),
    },
    ...(isAllOrgs
      ? [
          {
            key: 'org',
            header: 'Organization',
            hideBelow: 'xl' as const,
            cellClassName: 'text-muted text-sm',
            cell: (s: Submission) => s.org_name ?? '-',
          },
        ]
      : []),
    { key: 'status', header: 'Status', cell: (s) => <StatusBadge status={s.status} /> },
    {
      key: 'verdict',
      header: 'Verdict',
      cell: (s) => <VerdictBadge family={s.family} verdict={s.verdict} tags={s.tags} />,
    },
    {
      key: 'size',
      header: 'Size',
      hideBelow: 'xl',
      cellClassName: 'text-muted text-sm tabular-nums',
      cell: (s) => formatSubmittedSize(s.lane, s.sample_size),
    },
    // The same wall clock the submissions page shows - submission to
    // completion, queue wait included - with the run's budget under it. One
    // column carries both: the recent table has no Lane column for the
    // timeout to ride, and a second column would push the strip wide.
    {
      key: 'duration',
      header: 'Duration',
      hideBelow: 'lg',
      cellClassName: 'text-muted text-sm tabular-nums whitespace-nowrap',
      cell: (s) => {
        const timeout = formatTimeoutShort(s.timeout_seconds ?? 0);
        return (
          <div>
            <div>{formatWallDuration(s.submitted_at, s.completed_on)}</div>
            {timeout && <div className="text-xs">{timeout} timeout</div>}
          </div>
        );
      },
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cellClassName: 'cell-time text-sm tabular-nums whitespace-nowrap',
      cell: (s) => formatRelativeTime(s.submitted_at),
    },
    {
      key: 'go',
      header: '',
      align: 'right',
      cellClassName: 'cell-action',
      cell: () => <ArrowRight size={16} className="inline text-muted" aria-hidden />,
    },
  ];


  const completedLast24h = useMemo(() => {
    const now = Date.now();
    return subs.filter((s) => {
      if (s.status !== 'completed') return false;
      const ref = s.completed_on ? new Date(s.completed_on).getTime() : new Date(s.submitted_at).getTime();
      return now - ref < 24 * 3600 * 1000;
    }).length;
  }, [subs]);

  const quota = quotaQ.data;
  const used = quota?.used ?? 0;
  const quotaTotal = quota?.usage_limit ?? 0;
  const usedPct = quotaTotal > 0 ? (used / quotaTotal) * 100 : 0;

  const fleet = fleetQ.data;
  const fleetOrgs = fleet?.orgs ?? [];
  const fleetInFlight = fleetOrgs.reduce((s, o) => s + o.in_flight, 0);
  const fleetBusy = (fleet?.providers ?? []).reduce((s, p) => s + p.busy, 0);
  const fleetReady = (fleet?.providers ?? []).reduce((s, p) => s + p.ready, 0);
  const fleetMachines = (fleet?.providers ?? []).reduce((s, p) => s + p.total, 0);

  const pool = systemsQ.data ? poolTotals(systemsQ.data) : null;

  if (!orgId) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <h2>No organization yet</h2>
        <p className="text-muted mt-2">You aren't a member of any organization. Ask your admin to invite you.</p>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          {/* Greet by org, not by user: the picker beside it switches orgs,
              and the greeting doubles as confirmation of whose activity the
              page below is showing. The all-orgs view greets nobody - it is
              every tenant at once. */}
          <h1>{activeOrgName ? `Welcome, ${activeOrgName}!` : 'Overview'}</h1>
          <p className="text-muted">
            {isAllOrgs ? 'Platform-wide analysis activity.' : "Your organization's analysis activity."}
          </p>
        </div>
        <OrgPicker />
      </div>

      {/* Hero CTA */}
      <div className="card mb-6 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div
            className="shrink-0 p-3 rounded-xl"
            style={{ background: 'var(--status-info-bg)', color: 'var(--status-info)' }}
          >
            <Zap size={24} />
          </div>
          <div className="min-w-0">
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Submit samples programmatically</h3>
            <p className="text-muted text-sm mt-1">Mint a scoped API key and stream verdicts back to your pipeline.</p>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2">
          {primaryOrgId ? (
            <Link to={`/org/${primaryOrgId}/settings/api-keys`} className="btn-primary">
              <KeyRound size={16} /> Generate API key
            </Link>
          ) : (
            <Link to="/submit" className="btn-primary">
              <Upload size={16} /> Submit
            </Link>
          )}
          <Link to="/docs" className="btn-violet">
            <BookOpen size={16} /> See the API docs
          </Link>
        </div>
      </div>

      {/* The brand on exported reports, until one is set. Per org, so never
          on the all-orgs view, and only for whoever can set it; the prompt
          itself stays off the page once a label exists. Managing a set
          label lives on the organization's Settings page. */}
      {!isAllOrgs && canBrandOrg(user, orgId) && <OrgLogoPrompt orgId={orgId} />}

      {/* KPI Cards */}
      <div className="kpi-grid">
        {/* Activity strip (fair-share §5 option B): server-side counts from
            the org's own task rows - accurate past the paginated list. The
            all-orgs variant reads the fleet-wide inflight summary instead. */}
        <div className="card kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Activity</span>
            {(isAllOrgs
              ? fleetInFlight + (fleet?.queue_depth ?? 0)
              : (quota?.running ?? 0) + (quota?.queued ?? 0)) > 0 ? (
              <span className="status-indicator status-warning">Active</span>
            ) : (
              <span className="status-indicator status-good">Idle</span>
            )}
          </div>
          <div className="kpi-body">
            {isAllOrgs ? (
              <>
                <span className="kpi-value">{fleet ? fleetInFlight : '-'}</span>
                <span className="kpi-total">in flight · {fleet ? fleet.queue_depth : '-'} queued</span>
              </>
            ) : (
              <>
                <span className="kpi-value">{quota ? quota.running : '-'}</span>
                <span className="kpi-total">running · {quota ? quota.queued : '-'} queued</span>
              </>
            )}
          </div>
          <div className="kpi-footer">
            <Activity className="trend-icon" style={{ color: 'var(--text-muted)' }} />
            <span className="trend-text">
              {isAllOrgs
                ? 'Across all organizations'
                : pool && pool.total > 0
                  ? `Live queue state · ${pool.inUse} of ${pool.total} machines in use`
                  : 'Live queue state'}
            </span>
          </div>
        </div>

        <div className="card kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">{isAllOrgs ? 'Fleet' : quota ? `${periodLabel(quota.usage_period)} usage` : 'Usage'}</span>
          </div>
          <div className="kpi-body">
            {isAllOrgs ? (
              <>
                <span className="kpi-value">{fleet ? fleetBusy : '-'}</span>
                <span className="kpi-total">/ {fleet ? fleetMachines : '-'} machines busy</span>
              </>
            ) : (
              <>
                <span className="kpi-value">{used}</span>
                <span className="kpi-total">/ {quotaTotal || '∞'}</span>
              </>
            )}
          </div>
          <div className="kpi-footer">
            <HardDrive className="trend-icon" style={{ color: 'var(--text-muted)' }} />
            <span className="trend-text">{isAllOrgs ? `${fleetReady} ready for dispatch` : `${Math.round(usedPct)}% of quota used`}</span>
          </div>
        </div>

        {!isAllOrgs && (
          <MachinePoolCard
            images={systemsQ.data}
            loading={systemsQ.isFetching && systemsQ.data === undefined}
          />
        )}
      </div>

      {/* Per-org utilization (fair-share §5): the operator's capacity-
          planning signal - who is consuming the shared fleet right now. */}
      {isAllOrgs && (
        <FleetUtilizationCard
          orgs={fleetOrgs}
          totalInFlight={fleetInFlight}
          loading={fleetLoading}
          error={fleetQ.isError}
          onRetry={() => fleetQ.refetch()}
        />
      )}

      {/* Main Two Column Layout. The wrapper is the query container - the
          split has to react to its own width, not the viewport's, because the
          256px rail sits between them. */}
      <div className="dashboard-main">
        {/* Charts first: the landscape and this deployment's own throughput,
            side by side. The table beneath them takes the full width, which is
            what a six-column table wanted all along - in the old 2fr column it
            hid Size and Organization below xl. */}
        <div className="dashboard-charts">
          <div className="card p-5">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-ink-500 shrink-0" aria-hidden />
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Submissions</h2>
            </div>
            <p className="text-muted text-sm mt-1 mb-4">
              What this deployment analysed, per day for the last {SUBMISSION_CHART_DAYS} days.
            </p>
            <SubmissionsChart data={byDay} />
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2">
              <Bug className="w-4 h-4 text-ink-500 shrink-0" aria-hidden />
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>
                Top malware families in the wild
              </h2>
            </div>
            <p className="text-muted text-sm mt-1 mb-4">
              {activityQ.data?.families_accumulated
                ? 'Families the threat-intelligence feed has reported today. Each count is the most seen in one sample, not a daily total.'
                : 'What the threat-intelligence feed has been seeing in its most recent records.'}
            </p>
            {activityQ.data ? (
              <TopFamilies activity={activityQ.data} />
            ) : (
              <p className="text-muted text-sm">
                {activityQ.isError ? 'Could not reach the intelligence feed.' : 'Loading…'}
              </p>
            )}
          </div>
        </div>

        {/* Full width. The charts used to sit in a column beside this and the
            grid kept a 1fr track for them; with them moved above, that track
            was empty space to the right of the table. */}
        <div className="min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="min-w-0">
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Recent Submissions</h2>
                {/* The counts sit on the list they describe rather than in
                    tiles of their own. As separate cards they restated what is
                    already on screen and cost the row a chart could use. */}
                <p className="text-muted text-sm mt-1">
                  {!subsQ.isSuccess
                    ? '-'
                    : `${subs.length} all time · ${completedLast24h} completed in the last 24h`}
                </p>
              </div>
              <Link to="/submissions" className="btn-secondary">
                View All
              </Link>
            </div>

            {subsQ.isError ? (
              <ErrorState
                title="Couldn't load submissions"
                description="The orchestrator didn't respond. Try again."
                retry={() => subsQ.refetch()}
                compact
              />
            ) : (
              <ResponsiveTable<Submission>
                columns={recentColumns}
                rows={recent}
                rowKey={(s) => s.id}
                loading={subsLoading}
                rowHref={(s) => `/submissions/${s.id}`}
                onRowHover={(s) =>
                  qc.prefetchQuery({
                    queryKey: ['submission', s.id],
                    queryFn: () => api.getSubmission(s.id),
                    staleTime: 30_000,
                  })
                }
                emptyState={<EmptyState icon={Inbox} title="No submissions yet" compact />}
                renderCard={(s) => (
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 mt-0.5">{getFileIcon(s.target_name, undefined, s.target_kind)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-medium wrap-anywhere">{s.target_name}</span>
                        <DetonationTag kind={s.detonation_kind} lane={s.lane} />
                      </div>
                      <div className="cell-id text-xs mt-0.5">
                        <CopyableId id={s.malbox_task_id} className="-ml-0" />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                        <StatusBadge status={s.status} />
                        <VerdictBadge family={s.family} verdict={s.verdict} tags={s.tags} />
                        {isAllOrgs && (
                          <span className="text-muted text-xs">{s.org_name ?? '-'}</span>
                        )}
                        <span className="text-muted text-xs tabular-nums">
                          {formatSubmittedSize(s.lane, s.sample_size)}
                        </span>
                        {s.completed_on && (
                          <span className="text-muted text-xs tabular-nums">
                            {formatWallDuration(s.submitted_at, s.completed_on)}
                          </span>
                        )}
                        {(s.timeout_seconds ?? 0) > 0 && (
                          <span className="text-muted text-xs tabular-nums">
                            {formatTimeoutShort(s.timeout_seconds ?? 0)} timeout
                          </span>
                        )}
                        <span className="text-muted text-xs tabular-nums">
                          {formatRelativeTime(s.submitted_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              />
            )}
        </div>
      </div>
    </>
  );
}

function FleetUtilizationCard({ orgs, totalInFlight, loading, error, onRetry }: {
  orgs: OrgInflight[];
  totalInFlight: number;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const share = (o: OrgInflight) =>
    totalInFlight > 0 && o.in_flight > 0
      ? `${Math.round((o.in_flight / totalInFlight) * 100)}%`
      : '-';

  const columns: Column<OrgInflight>[] = [
    { key: 'org', header: 'Organization', cellClassName: 'font-medium', cell: (o) => o.org_name },
    { key: 'inflight', header: 'In Flight', cellClassName: 'tabular-nums', cell: (o) => o.in_flight },
    { key: 'queued', header: 'Queued', cellClassName: 'tabular-nums', cell: (o) => o.queued },
    {
      key: 'share',
      header: 'Utilization of Fleet',
      cellClassName: 'tabular-nums',
      cell: share,
    },
  ];

  return (
    <div className="mb-6">
      <h2 className="mb-4" style={{ fontSize: '1.125rem', fontWeight: 600 }}>
        Organization Utilization
      </h2>
      {error ? (
        <ErrorState
          title="Couldn't load utilization"
          description="The orchestrator didn't respond. Try again."
          retry={onRetry}
          compact
        />
      ) : (
        <ResponsiveTable<OrgInflight>
          columns={columns}
          rows={orgs}
          rowKey={(o) => o.org_id}
          loading={loading}
          emptyState={<EmptyState title="No live analyses" compact />}
          renderCard={(o) => (
            <div>
              <div className="font-medium truncate">{o.org_name}</div>
              <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <dt className="text-muted">In flight</dt>
                  <dd className="tabular-nums mt-0.5">{o.in_flight}</dd>
                </div>
                <div>
                  <dt className="text-muted">Queued</dt>
                  <dd className="tabular-nums mt-0.5">{o.queued}</dd>
                </div>
                <div>
                  <dt className="text-muted">Fleet share</dt>
                  <dd className="tabular-nums mt-0.5">{share(o)}</dd>
                </div>
              </dl>
            </div>
          )}
        />
      )}
    </div>
  );
}
